import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Crown,
  ExternalLink,
  Folder,
  Gift,
  Info,
  Loader2,
  UserRound,
  UsersRound,
  ListTodo,
  ArrowUpRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { fetchGraphQL } from '@/relay/fetchGraphQL';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { CheckoutRedirectDialog } from '../components/CheckoutRedirectDialog';
import { BillingIntervalToggle } from '../components/BillingIntervalToggle';
import { PlanModal } from '../components/PlanModal';
import {
  PRO_PAGE_FEATURES,
  billingIntervalLabel,
  proChargeUsd,
  proPricePresentation,
  type AppBillingInterval,
} from '../billingPlans';
import {
  captureEvent,
  createEventId,
  normalizeErrorCode,
} from '@/analytics/posthog';
import { trackMetaEvent } from '@/analytics/metaPixel';
import { useTrackingConsent } from '@/analytics/consent';

const CHECKOUT_EVENT_ID_KEY = 'daysheetly_checkout_event_id';

function readSessionValue(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSessionValue(key: string, value: string) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Analytics must never prevent billing from completing.
  }
}

function removeSessionValue(key: string) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Analytics must never prevent billing from completing.
  }
}

const billingSelection = `
  plan
  effectivePlan
  status
  currentPeriodEnd
  cancelAtPeriodEnd
  pastDueSince
  canManage
  billingInterval
  limits {
    workspaces
    projectsPerWorkspace
    invitedAccountsPerWorkspace
    tasksPerWorkspace
  }
  usage {
    ownedWorkspaces
    projects
    invitedAccounts
  }
`;

const workspaceBillingQuery = `
  query SubscriptionPageBilling {
    workspaceBilling {
      ${billingSelection}
    }
  }
`;

const createPortalMutation = `
  mutation SubscriptionPageCreatePortal {
    createCustomerPortalSession {
      url
    }
  }
`;

const createCheckoutMutation = `
  mutation SubscriptionPageCreateCheckout($interval: BillingInterval!) {
    createProCheckoutSession(interval: $interval) {
      url
      sessionId
    }
  }
`;

const switchIntervalMutation = `
  mutation SubscriptionPageSwitchInterval($interval: BillingInterval!) {
    switchProBillingInterval(interval: $interval) {
      ${billingSelection}
    }
  }
`;

interface BillingData {
  plan: 'FREE' | 'PRO';
  effectivePlan: 'FREE' | 'PRO';
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  pastDueSince: string | null;
  canManage: boolean;
  billingInterval?: AppBillingInterval | null;
  limits: {
    workspaces: number;
    projectsPerWorkspace: number;
    invitedAccountsPerWorkspace: number;
    tasksPerWorkspace: number;
  };
  usage: {
    ownedWorkspaces: number;
    projects: number;
    invitedAccounts: number;
  };
}

interface GraphQLResult<T> {
  data?: T;
  errors?: Array<{ message?: string }>;
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(value));
}

function limitLabel(value: number): string {
  return value === -1 ? 'Unlimited' : String(value);
}

function usagePercent(usage: number, limit: number): number {
  if (limit <= 0 || limit === -1) return 0;
  return Math.min(100, (usage / limit) * 100);
}

export function SubscriptionPage() {
  const { activeWorkspaceId } = useWorkspace();
  const trackingConsent = useTrackingConsent();
  const [billing, setBilling] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [intervalBusy, setIntervalBusy] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState<AppBillingInterval>('MONTHLY');
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const purchaseCaptured = useRef(false);
  const switchRequest = useRef(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = (await fetchGraphQL(
        { text: workspaceBillingQuery } as never,
        {},
      )) as GraphQLResult<{ workspaceBilling: BillingData | null }>;
      if (response.errors?.length) throw new Error(response.errors[0]?.message || 'Could not load subscription');
      const nextBilling = response.data?.workspaceBilling ?? null;
      setBilling(nextBilling);
      if (nextBilling?.billingInterval === 'MONTHLY' || nextBilling?.billingInterval === 'ANNUAL') {
        setSelectedInterval(nextBilling.billingInterval);
      }
      return nextBilling;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not load subscription');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeWorkspaceId) void refresh();
    else setLoading(false);
  }, [activeWorkspaceId, refresh]);

  const checkoutState = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('checkout');
  }, []);

  useEffect(() => {
    if (
      checkoutState !== 'success'
      || !activeWorkspaceId
      || billing?.effectivePlan === 'PRO'
    ) {
      return;
    }

    let attempts = 0;
    let timer: number | undefined;
    const poll = () => {
      attempts += 1;
      timer = window.setTimeout(async () => {
        const nextBilling = await refresh();
        if (nextBilling?.effectivePlan !== 'PRO' && attempts < 10) poll();
      }, 3000);
    };

    poll();
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [activeWorkspaceId, billing?.effectivePlan, checkoutState, refresh]);

  useEffect(() => {
    if (
      checkoutState !== 'success'
      || billing?.effectivePlan !== 'PRO'
      || trackingConsent !== 'granted'
      || !activeWorkspaceId
      || purchaseCaptured.current
    ) {
      return;
    }

    const dedupeKey = `daysheetly_purchase_captured:${activeWorkspaceId}`;
    if (readSessionValue(dedupeKey)) {
      purchaseCaptured.current = true;
      return;
    }

    const purchaseValue = billing.billingInterval === 'ANNUAL' ? proChargeUsd('ANNUAL') : proChargeUsd('MONTHLY');
    const eventId = readSessionValue(CHECKOUT_EVENT_ID_KEY) ?? createEventId('purchase');
    captureEvent('subscription_activated', {
      plan: 'pro',
      provider: 'stripe',
      value: purchaseValue,
      currency: 'USD',
      status: 'active',
      event_id: eventId,
    });
    trackMetaEvent(
      'Purchase',
      { value: purchaseValue, currency: 'USD', content_name: 'pro_subscription' },
      eventId,
    );
    writeSessionValue(dedupeKey, '1');
    removeSessionValue(CHECKOUT_EVENT_ID_KEY);
    purchaseCaptured.current = true;
  }, [activeWorkspaceId, billing?.billingInterval, billing?.effectivePlan, checkoutState, trackingConsent]);

  useEffect(() => {
    const resetCheckoutBusy = () => {
      setCheckoutBusy(false);
      setPlanModalOpen(false);
    };

    // Stripe navigation can restore this page from the browser back-forward
    // cache without remounting it. Clear the transient state whenever the
    // page is restored or history navigation returns to the app.
    window.addEventListener('pageshow', resetCheckoutBusy);
    window.addEventListener('pagehide', resetCheckoutBusy);
    window.addEventListener('popstate', resetCheckoutBusy);

    return () => {
      window.removeEventListener('pageshow', resetCheckoutBusy);
      window.removeEventListener('pagehide', resetCheckoutBusy);
      window.removeEventListener('popstate', resetCheckoutBusy);
    };
  }, []);

  const openPortal = async () => {
    setBusy(true);
    try {
      const response = (await fetchGraphQL(
        { text: createPortalMutation } as never,
        {},
      )) as GraphQLResult<{ createCustomerPortalSession: { url: string } }>;
      if (response.errors?.length) throw new Error(response.errors[0]?.message || 'Could not open billing portal');
      const url = response.data?.createCustomerPortalSession.url;
      if (!url) throw new Error('Stripe did not return a billing portal URL');
      window.location.assign(url);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not open billing portal');
      setBusy(false);
    }
  };

  const startCheckout = async (interval: AppBillingInterval) => {
    setPlanModalOpen(false);
    setCheckoutBusy(true);
    const eventId = createEventId('checkout');
    const chargeUsd = proChargeUsd(interval);
    try {
      const response = (await fetchGraphQL(
        { text: createCheckoutMutation } as never,
        { interval },
      )) as GraphQLResult<{ createProCheckoutSession: { url: string } }>;
      if (response.errors?.length) throw new Error(response.errors[0]?.message || 'Could not start Checkout');
      const checkout = response.data?.createProCheckoutSession;
      const url = checkout?.url;
      if (!url) throw new Error('Stripe did not return a Checkout URL');
      writeSessionValue(CHECKOUT_EVENT_ID_KEY, eventId);
      captureEvent('checkout_started', {
        plan: 'pro',
        provider: 'stripe',
        value: chargeUsd,
        currency: 'USD',
        event_id: eventId,
      });
      trackMetaEvent(
        'InitiateCheckout',
        { value: chargeUsd, currency: 'USD', content_name: 'pro_subscription' },
        eventId,
      );
      window.location.assign(url);
    } catch (error) {
      captureEvent('checkout_failed', {
        plan: 'pro',
        provider: 'stripe',
        event_id: eventId,
        error_code: normalizeErrorCode(error instanceof Error ? error.message : ''),
      });
      toast.error(error instanceof Error ? error.message : 'Could not start Checkout');
      setCheckoutBusy(false);
      setPlanModalOpen(true);
    }
  };

  const switchInterval = async (interval: AppBillingInterval, previous: AppBillingInterval) => {
    const requestId = switchRequest.current + 1;
    switchRequest.current = requestId;
    setIntervalBusy(true);
    try {
      const response = (await fetchGraphQL(
        { text: switchIntervalMutation } as never,
        { interval },
      )) as GraphQLResult<{ switchProBillingInterval: BillingData }>;
      if (switchRequest.current !== requestId) return;
      if (response.errors?.length) throw new Error(response.errors[0]?.message || 'Could not switch billing interval');
      const nextBilling = response.data?.switchProBillingInterval;
      if (!nextBilling) throw new Error('Could not switch billing interval');
      setBilling(nextBilling);
      if (nextBilling.billingInterval === 'MONTHLY' || nextBilling.billingInterval === 'ANNUAL') {
        setSelectedInterval(nextBilling.billingInterval);
      } else {
        setSelectedInterval(interval);
      }
    } catch (error) {
      if (switchRequest.current !== requestId) return;
      toast.error(error instanceof Error ? error.message : 'Could not switch billing interval');
      setSelectedInterval(previous);
    } finally {
      if (switchRequest.current === requestId) setIntervalBusy(false);
    }
  };

  const handleIntervalChange = (interval: AppBillingInterval) => {
    if (!billing?.canManage || interval === selectedInterval || intervalBusy || checkoutBusy) return;
    const previous = selectedInterval;
    setSelectedInterval(interval);
    if (billing.effectivePlan === 'PRO') {
      void switchInterval(interval, previous);
    }
  };

  if (loading) {
    return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  if (!billing) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6">
        <Card><CardContent className="py-10 text-center text-muted-foreground">Select a workspace to manage its subscription.</CardContent></Card>
      </div>
    );
  }

  const isPro = billing.effectivePlan === 'PRO';
  const isPastDue = billing.status === 'past_due';
  const currentPeriodEnd = formatDate(billing.currentPeriodEnd);
  const proPrice = proPricePresentation(selectedInterval);

  const usageItems = [
    {
      label: 'Workspaces',
      value: billing.usage.ownedWorkspaces,
      limit: billing.limits.workspaces,
      icon: UsersRound,
    },
    {
      label: 'Projects per workspace',
      value: billing.usage.projects,
      limit: billing.limits.projectsPerWorkspace,
      icon: Folder,
    },
    {
      label: 'Invited accounts',
      value: billing.usage.invitedAccounts,
      limit: billing.limits.invitedAccountsPerWorkspace,
      icon: UserRound,
    },
    {
      label: 'Tasks per workspace',
      value: null,
      limit: billing.limits.tasksPerWorkspace,
      icon: ListTodo,
    },
  ];

  return (
    <div data-testid="subscription-page" className="flex w-full min-w-0 flex-col gap-4 px-4 py-5 sm:gap-6 sm:px-6 sm:py-6 lg:py-8">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <p className="flex items-center gap-2 text-sm font-medium text-primary">
            <Info className="h-4 w-4" aria-hidden="true" />
            Workspace billing
          </p>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Subscription</h1>
          <p className="mt-1 text-sm text-muted-foreground">Manage the plan and limits for this workspace.</p>
        </div>
        <Badge className="self-start" variant={isPastDue ? 'warning' : isPro ? 'success' : 'secondary'}>
          {isPro && !isPastDue ? 'PRO PLAN' : isPastDue ? 'PAST DUE' : 'FREE PLAN'}
        </Badge>
      </div>

      {checkoutState === 'success' ? (
        <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 sm:p-4">
          <CreditCard className="mt-0.5 h-5 w-5 shrink-0" />
          <p>Checkout completed. Stripe is confirming the subscription; this page updates when the webhook arrives.</p>
        </div>
      ) : null}

      {checkoutState === 'cancelled' ? (
        <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 sm:p-4">
          <CreditCard className="mt-0.5 h-5 w-5 shrink-0" />
          <p>Checkout cancelled. No charge was made.</p>
        </div>
      ) : null}

      {isPastDue ? (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 sm:p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <p>Payment needs attention. Pro limits remain available for the three-day grace period. Update the payment method in Stripe to keep Pro active.</p>
        </div>
      ) : null}

      <div className="grid min-w-0 items-stretch gap-4 sm:gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.9fr)]">
        <Card className="h-full">
          <CardHeader className="gap-4 border-b p-4 pb-4 sm:gap-5 sm:p-6 sm:pb-6 sm:flex-row sm:items-start">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-primary/15 bg-primary/10 text-primary sm:h-16 sm:w-16">
              {isPro ? <Crown className="h-7 w-7 sm:h-8 sm:w-8" aria-hidden="true" /> : <Gift className="h-7 w-7 sm:h-8 sm:w-8" aria-hidden="true" />}
            </div>
            <div className="min-w-0 space-y-1">
              <CardTitle className="text-xl">{isPro ? 'Pro plan' : 'Free plan'}</CardTitle>
              <CardDescription className="max-w-md text-sm leading-6">
                {isPro
                  ? 'Your workspace has the Pro limits: 4 workspaces, unlimited projects, 10 invited accounts, and task migration between workspaces.'
                  : "You're on the Free plan: 1 workspace, unlimited projects, and 2 invited accounts. Upgrade for 4 workspaces, 10 invited accounts, and task migration between workspaces."}
              </CardDescription>
              {billing.billingInterval === 'MONTHLY' || billing.billingInterval === 'ANNUAL' ? (
                <p data-testid="workspace-billing-interval" className="pt-1 text-xs text-muted-foreground">
                  Billing interval: {billingIntervalLabel(billing.billingInterval)}
                </p>
              ) : null}
              {currentPeriodEnd ? (
                <p className="pt-1 text-xs text-muted-foreground">
                  {billing.cancelAtPeriodEnd ? `Ends ${currentPeriodEnd}` : `Renews ${currentPeriodEnd}`}
                </p>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-5 p-4 pt-4 sm:space-y-6 sm:p-6 sm:pt-6">
            <div>
              <h2 className="text-base font-semibold">Current usage</h2>
              <p className="mt-1 text-sm text-muted-foreground">Limits are applied per workspace.</p>
            </div>
            <div className="space-y-4 sm:space-y-5">
              {usageItems.map(({ label, value, limit, icon: Icon }) => {
                const overLimit = value !== null && limit !== -1 && value > limit;
                return (
                  <div key={label} className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/15 bg-primary/5 text-primary sm:h-11 sm:w-11">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium">{label}</p>
                        <p className="shrink-0 text-sm font-semibold">
                          {value === null ? limitLabel(limit) : `${value} / ${limitLabel(limit)}`}
                        </p>
                      </div>
                      {value !== null && limit !== -1 ? (
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted" aria-label={`${label} usage`}>
                          <div className="h-full rounded-full bg-primary" style={{ width: `${usagePercent(value, limit)}%` }} />
                        </div>
                      ) : (
                        <div className="mt-2 h-2 rounded-full bg-primary/10" />
                      )}
                      {overLimit ? (
                        <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700">
                          <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                          You&apos;re using more than included.
                        </p>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="flex h-full min-w-0 flex-col border-primary/20 bg-primary/[0.035]">
          <CardHeader className="gap-4 border-b p-4 pb-4 sm:p-6 sm:pb-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full border border-primary/20 bg-background text-primary shadow-sm sm:h-16 sm:w-16">
                <Crown className="h-7 w-7 sm:h-8 sm:w-8" aria-hidden="true" />
              </div>
              {isPro ? <Badge variant="success">ACTIVE</Badge> : <Badge variant="secondary">UPGRADE</Badge>}
            </div>
            <div>
              <CardTitle className="text-xl">Pro plan</CardTitle>
              <div className="mt-3">
                <BillingIntervalToggle
                  value={selectedInterval}
                  onChange={handleIntervalChange}
                  disabled={!billing.canManage || intervalBusy || checkoutBusy}
                />
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span data-testid="pro-price" className="text-3xl font-bold tracking-tight text-primary">{proPrice.amount}</span>
                <span className="text-sm text-muted-foreground">{proPrice.cadence}</span>
              </div>
              <p data-testid="pro-price-detail" className="mt-1 text-sm text-muted-foreground">{proPrice.detail}</p>
              {intervalBusy ? <p className="mt-1 text-xs text-muted-foreground">Updating billing interval...</p> : null}
              <CardDescription className="mt-2 text-sm leading-6">Unlimited projects. Pro adds workspaces, invited accounts, and task migration.</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col gap-5 p-4 pt-4 sm:gap-6 sm:p-6 sm:pt-6">
            <ul className="space-y-3.5 sm:space-y-4">
              {PRO_PAGE_FEATURES.map((feature) => (
                <li key={feature} className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto space-y-3">
              {billing.canManage ? (
                isPro ? (
                  <Button type="button" variant="outline" className="w-full" onClick={() => void openPortal()} disabled={busy}>
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />}
                    Open Stripe billing portal
                  </Button>
                ) : (
                  <Button data-testid="subscription-upgrade-button" type="button" className="w-full" onClick={() => setPlanModalOpen(true)} disabled={checkoutBusy}>
                    {checkoutBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowUpRight className="mr-2 h-4 w-4" />}
                    {checkoutBusy ? 'Opening Checkout...' : 'Upgrade to Pro'}
                  </Button>
                )
              ) : (
                <p className="rounded-lg border border-border bg-background/70 p-3 text-center text-sm text-muted-foreground">
                  Only the workspace owner can manage billing.
                </p>
              )}
              {billing.cancelAtPeriodEnd ? <p className="text-center text-xs text-muted-foreground">Cancellation is scheduled for the end of the period.</p> : null}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-2 border-t pt-3 text-sm text-muted-foreground sm:pt-4">
        <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>Plans apply to this workspace.</span>
      </div>

      <PlanModal
        open={planModalOpen}
        onOpenChange={setPlanModalOpen}
        interval={selectedInterval}
        onIntervalChange={handleIntervalChange}
        onUpgrade={(interval) => void startCheckout(interval)}
        loading={checkoutBusy}
      />
      <CheckoutRedirectDialog open={checkoutBusy} />
    </div>
  );
}
